import { useState, useMemo } from 'react';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { downloadFile } from '../../lib/api.js';
import { useOrders } from '../../lib/useBusinessData.js';
import { dateLong } from '../../lib/utils.js';
import { Button, IconButton } from '../../components/ui/Button.jsx';
import { DocStatusChip } from '../../components/ui/Chips.jsx';
import { MoneyCell } from '../../components/ui/Misc.jsx';
import { PageShell } from '../../components/layout/PageShell.jsx';
import { matches, exportCsv, SearchInput, SimpleTable, RowActions } from './shared.jsx';

// ─── Order detail slide-in panel ─────────────────────────────────────────────

function stubLineItems(order) {
  const a = +(order.total * 0.60).toFixed(2);
  const b = +(order.total * 0.25).toFixed(2);
  const c = +(order.total - a - b).toFixed(2);
  return [
    { desc: 'Software License (annual)', qty: 1, unit: a, line: a },
    { desc: 'Implementation Services',    qty: 1, unit: b, line: b },
    { desc: 'Support & Maintenance',      qty: 1, unit: c, line: c },
  ];
}

function PanelRow({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.04em', width: 76, flex: 'none' }}>{label}</span>
      <span style={{ fontSize: 13, color: C.fg }}>{children}</span>
    </div>
  );
}

function OrderDetailPanel({ order, onClose }) {
  const items = stubLineItems(order);
  const fmt = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [progress, setProgress] = useState(null);
  const downloading = progress !== null;

  async function handleDownload() {
    if (downloading) return;
    setProgress(true);
    try {
      await downloadFile(`/orders/${order.id}/pdf`, `${order.no}.pdf`, setProgress);
    } catch {
      // download failed — button re-enables below so the user can retry
    } finally {
      setProgress(null);
    }
  }

  return (
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, width: 'min(420px, 100vw)', height: '100vh',
        background: C.tin, borderLeft: `1px solid ${C.mercury}`,
        boxShadow: '-4px 0 20px rgba(0,0,0,0.10)', zIndex: 101,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.mercury}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'Roboto Mono', fontSize: 13, color: C.liquid, fontWeight: 500 }}>{order.no}</span>
          <DocStatusChip id={order.status} size="sm" />
          <span style={{ flex: 1 }} />
          <IconButton icon="download" title={downloading ? 'Downloading…' : 'Download PDF'}
            progress={downloading ? progress : undefined} disabled={downloading} onClick={handleDownload} />
          <IconButton icon="close" title="Close" onClick={onClose} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <PanelRow label="Customer">{order.customer}</PanelRow>
            <PanelRow label="Date">{dateLong(order.date)}</PanelRow>
            <PanelRow label="Currency">{order.currency}</PanelRow>
          </div>

          <div style={{ height: 1, background: C.mercury }} />

          <div>
            <div style={{ fontSize: 11, color: C.iron, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Line Items
            </div>
            <div style={{ border: `1px solid ${C.mercury}`, borderRadius: 4, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ background: C.platinum }}>
                  <tr>
                    {['Description', 'Qty', 'Unit Price', 'Total'].map((h, i) => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: i > 0 ? 'right' : 'left', fontSize: 10, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.05em', width: i > 0 ? 80 : undefined }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.mercury}`, background: i % 2 === 1 ? C.chrome : C.tin }}>
                      <td style={{ padding: '8px 10px', color: C.fg }}>{item.desc}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: C.iron }}>{item.qty}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}><MoneyCell amount={item.unit} currency={order.currency} /></td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}><MoneyCell amount={item.line} currency={order.currency} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14, paddingTop: 12, borderTop: `2px solid ${C.mercury}` }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
            <span style={{ fontFamily: 'Roboto Mono', fontSize: 15, fontWeight: 600, color: C.deepsea }}>
              {order.currency} {fmt.format(order.total)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

export function OrdersPage() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const { orders, loading } = useOrders();
  const filtered = useMemo(() => orders.filter(r => matches(search, r.no, r.customer)), [orders, search]);

  const cols = [
    { key: 'no',     label: 'Order No.', render: r => <a style={{ color: C.liquid, fontFamily: 'Roboto Mono', fontWeight: 500 }}>{r.no}</a> },
    { key: 'date',   label: 'Date',      render: r => <span style={{ color: C.iron }}>{dateLong(r.date)}</span> },
    { key: 'status', label: 'Status',    render: r => <DocStatusChip id={r.status} size="sm" /> },
    { key: 'total',  label: 'Total', align: 'right', csvValue: r => r.total, render: r => <MoneyCell amount={r.total} currency={r.currency} /> },
    { key: 'actions', label: '', width: 80, align: 'right', render: r => <RowActions kind="order" id={r.id} no={r.no} /> },
  ];

  return (
    <PageShell title={t('nav.orders')} subtitle="Sales orders raised on your account."
      actions={
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search order no., customer…" />
          <Button kind="outline" icon="download" onClick={() => exportCsv(filtered, cols, 'orders.csv')}>Export CSV</Button>
        </div>
      }>
      <SimpleTable columns={cols} rows={filtered} onRowClick={setSelected} loading={loading} />
      {selected && <OrderDetailPanel order={selected} onClose={() => setSelected(null)} />}
    </PageShell>
  );
}
